// mission-deploy-target.ts
//
// V2 spec §4.4 (V2-4): the deploy target the PERSON declared in the init
// request (`deploy_target`, startbuild's `a7-deploy`), persisted beside the
// other init provenance files (mission-declared-resources.ts shape: from the
// validated request, in the control root a worker cannot write). Written ONLY
// when declared — a v1 request leaves the control root as the baseline did.
// Read by `run` to print the deploy hint (E9); never feeds G3 or any hash.
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { writeFileDurable } from "./durable-file-writer.js";
import { DEPLOY_TARGETS, type DeployTarget } from "./schemas/init-schema.js";

const DEPLOY_TARGET_FILE_NAME = "mission-deploy-target.json";

export async function writeMissionDeployTarget(
  controlRoot: string,
  deployTarget: DeployTarget,
  now: () => Date,
): Promise<void> {
  const payload = {
    schema_version: 1,
    deploy_target: deployTarget,
    declared_at: now().toISOString(),
  };
  await writeFileDurable(
    join(controlRoot, DEPLOY_TARGET_FILE_NAME),
    Buffer.from(`${JSON.stringify(payload, null, 2)}\n`, "utf8"),
  );
}

/** `undefined` = not declared (v1 init) or unreadable/hand-edited into an unknown value. */
export async function readMissionDeployTarget(
  controlRoot: string,
): Promise<DeployTarget | undefined> {
  try {
    const raw = await readFile(join(controlRoot, DEPLOY_TARGET_FILE_NAME), "utf8");
    const value = (JSON.parse(raw) as { deploy_target?: unknown }).deploy_target;
    return DEPLOY_TARGETS.find((t) => t === value);
  } catch {
    return undefined;
  }
}

/**
 * §4.4 hint for a mission that reached `done` without `config.release`;
 * `undefined` when there is nothing to deploy (`local-only` / undeclared).
 */
export function describeDeployTargetHint(
  deployTarget: DeployTarget | undefined,
  /** The config file this run loaded (review L4: AVATAR_HOME may move it — never a hard-coded `~`). */
  globalConfigPath: string,
): string | undefined {
  if (deployTarget === undefined || deployTarget === "local-only") return undefined;
  return (
    `Deploy target = ${deployTarget}. Để deploy: thêm \`release\` vào ${globalConfigPath} ` +
    "(deploy_recipe + verify_recipe) rồi `avatar autopilot run` lại ⇒ xin G3."
  );
}
